import { Cloud, Folder, Lock, Zap } from "lucide-react";
import { Link } from "react-router-dom";

export default function About() {
  const styles = {
    aboutPage: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: "1.6",
      color: "#1a1a1a",
      background: "linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)",
      minHeight: "100vh",
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 20px",
    },
    header: {
      padding: "20px 0",
    },
    headerNav: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    logo: {
      display: "inline-flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "28px",
      fontWeight: 700,
      color: "#d97706",
      textDecoration: "none",
    },
    logoIcon: {
      width: "36px",
      height: "36px",
    },
    backButton: {
      background: "rgba(255, 255, 255, 0.2)",
      color: "#d97706",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      padding: "10px 20px",
      borderRadius: "25px",
      fontWeight: 600,
      fontSize: "14px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textDecoration: "none",
      display: "inline-block",
    },
    main: {
      padding: "40px 0",
    },
    heroSection: {
      textAlign: "center",
      marginBottom: "80px",
    },
    pageTitle: {
      fontSize: "3.5rem",
      fontWeight: 700,
      color: "white",
      marginBottom: "20px",
      lineHeight: "1.1",
    },
    pageSubtitle: {
      fontSize: "1.3rem",
      color: "rgba(255, 255, 255, 0.9)",
      maxWidth: "600px",
      margin: "0 auto",
    },
    contentSection: {
      background: "white",
      borderRadius: "20px",
      padding: "60px 40px",
      marginBottom: "40px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
    },
    sectionTitle: {
      fontSize: "2.5rem",
      fontWeight: 700,
      color: "#1a1a1a",
      marginBottom: "30px",
      textAlign: "center",
    },
    storyText: {
      fontSize: "1.1rem",
      lineHeight: "1.8",
      color: "#333",
      marginBottom: "30px",
      textAlign: "center",
      maxWidth: "800px",
      margin: "0 auto 30px",
    },
    valuesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "40px",
      marginTop: "50px",
    },
    valueCard: {
      textAlign: "center",
      padding: "30px 20px",
    },
    valueIcon: {
      width: "60px",
      height: "60px",
      margin: "0 auto 20px",
      background: "linear-gradient(135deg, #f59e0b, #d97706)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
    },
    valueIconSvg: {
      width: "28px",
      height: "28px",
    },
    valueTitle: {
      fontSize: "1.3rem",
      fontWeight: 600,
      marginBottom: "15px",
      color: "#1a1a1a",
    },
    valueDescription: {
      color: "#666",
      lineHeight: "1.6",
    },
    teamSection: {
      background: "rgba(255, 255, 255, 0.1)",
      borderRadius: "20px",
      padding: "60px 40px",
      textAlign: "center",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
    teamTitle: {
      fontSize: "2.5rem",
      fontWeight: 700,
      color: "white",
      marginBottom: "20px",
    },
    teamText: {
      fontSize: "1.1rem",
      color: "rgba(255, 255, 255, 0.9)",
      lineHeight: "1.8",
      maxWidth: "600px",
      margin: "0 auto",
    },
    footer: {
      textAlign: "center",
      padding: "40px 0",
      color: "rgba(255, 255, 255, 0.8)",
      fontSize: "0.9rem",
    },
  };

  return (
    <div style={styles.aboutPage}>
      <style>
        {`
          .back-button:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: translateY(-1px);
          }
          
          @media (max-width: 768px) {
            .page-title {
              font-size: 2.5rem !important;
            }
            
            .page-subtitle {
              font-size: 1.1rem !important;
            }
            
            .section-title {
              font-size: 2rem !important;
            }
            
            .content-section {
              padding: 40px 20px !important;
            }
            
            .team-section {
              padding: 40px 20px !important;
            }
            
            .values-grid {
              grid-template-columns: 1fr !important;
              gap: 30px !important;
            }
          }
          
          @media (max-width: 480px) {
            .page-title {
              font-size: 2rem !important;
            }
            
            .headerNav {
              flex-direction: column;
              gap: 20px;
            }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.container}>
          <div style={styles.headerNav} className="headerNav">
            <Link to="/" style={styles.logo}>
              <Cloud style={styles.logoIcon} />
              <span>MinDrop</span>
            </Link>
            <Link to="/" style={styles.backButton} className="back-button">
              ← Back to MinDrop
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.container}>
          {/* Hero Section */}
          <section style={styles.heroSection}>
            <h1 style={styles.pageTitle} className="page-title">
              About <span style={{ color: "#d97706" }}>MinDrop</span>
            </h1>
            <p style={styles.pageSubtitle} className="page-subtitle">
              Smart Notes. No Tape Required.
            </p>
          </section>

          {/* Our Story Section */}
          <section style={styles.contentSection} className="content-section">
            <h2 style={styles.sectionTitle} className="section-title">
              Our Story
            </h2>
            <p style={styles.storyText}>
              MinDrop is a secure, note-taking app designed for simplicity and
              privacy. It keep your notes safe and ensures only you can access
              them. With a clean interface and no unnecessary features, MinDrop
              helps you focus on your thoughts whether you're journaling,
              planning, or capturing quick ideas. Accessible across devices,
              it’s your private space in the cloud.
            </p>
          </section>

          {/* Our Values Section */}
          <section style={styles.contentSection} className="content-section">
            <h2 style={styles.sectionTitle} className="section-title">
              Our Values
            </h2>
            <div style={styles.valuesGrid} className="values-grid">
              <div style={styles.valueCard}>
                <div style={styles.valueIcon}>
                  <Lock style={styles.valueIconSvg} />
                </div>
                <h3 style={styles.valueTitle}>Secure Storage</h3>
                <p style={styles.valueDescription}>
                  Your notes are safe & secure with MinDrop. Only you have the
                  keys to your thoughts. No other user can access your notes.
                </p>
              </div>
              <div style={styles.valueCard}>
                <div style={styles.valueIcon}>
                  <Folder style={styles.valueIconSvg} />
                </div>
                <h3 style={styles.valueTitle}>Organized Notes</h3>
                <p style={styles.valueDescription}>
                  MinDrop makes it easy to keep your notes organized, secure,
                  and accessible. With a clean layout and simple structure, you
                  can sort your thoughts effortlessly
                </p>
              </div>
              <div style={styles.valueCard}>
                <div style={styles.valueIcon}>
                  <Zap style={styles.valueIconSvg} />
                </div>
                <h3 style={styles.valueTitle}>Fast Search</h3>
                <p style={styles.valueDescription}>
                  Find your notes instantly by title or tags. MinDrop’s smart
                  search makes organizing and retrieving your thoughts
                  effortless.
                </p>
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section style={styles.teamSection} className="team-section">
            <h2 style={styles.teamTitle}> 💌 Built with ❤️</h2>
            <p style={styles.teamText}>
              For People Who Forget Stuff, MinDrop was made for everyone who’s
              ever written a “note to self” and immediately lost it. Fast,
              private, and a little too organized, because your brain deserves a
              break.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.container}>
          <p>MinDrop - Built for your thoughts</p>
        </div>
      </footer>
    </div>
  );
}
